import React, { useState } from "react"
import { useParams } from "react-router-dom"
import GridTypeA from "components/grids/GridTypeA/GridTypeA"
import { Tag } from "components-design-system"
import DecisionTreeModal from "components/react-flow/DecisionTreeModal/DecisionTreeModal"
import "./AssessmentsGrid.scss"

// cell Renderers
import AssessmentLinkCellRenderer from "./AssessmentLinkCellRenderer/AssessmentLinkCellRenderer"
import AssessmentResultCellRenderer from "components/ag-grid-cell-renderers/AssessmentResultCellRenderer/AssessmentResultCellRenderer"
import DueDateCellRenderer from "./DueDateCellRenderer/DueDateCellRenderer"
import ActivityCellRendererTable from "./ActivityCellRendererTable/ActivityCellRendererTable"

// api
import { useGetActivityTypes } from "api/hooks/activities/useActivitiesApi"
import { useGetAssessmentTypes } from "api/hooks/admin/useAssessmentTypesApi"
import { useGetStudyAssessment } from "api/hooks/studies/useAssessmentsApi"

const AssessmentsGrid = ({
  gridRef,
  rowData,
  onFilterChanged,
  permissions,
  toggleCreateActivityModal,
  setMissingFields,
  onGridReady,
}) => {
  const { studyId } = useParams()

  // activities
  const { data: activityTypes } = useGetActivityTypes()

  // decision tree modal
  const [treeModalOpen, setTreeModalOpen] = useState(false)
  // selected decision tree
  const { data: assessmentTypes } = useGetAssessmentTypes()
  const [selectedDt, setSelectedDt] = useState(null)
  // selected assessment metadata
  const [selectedAssessId, setSelectedAssessId] = useState()
  const { data: selectedAssessment } = useGetStudyAssessment(studyId, selectedAssessId)

  const getSelectedDt = (assessmentName) => {
    return assessmentTypes?.find((type) => type.name === assessmentName)?.dt
  }

  /**
   * Steps to open decision tree modal:
   * Get decision tree from assessment types (plural) api.
   * Get assessment's metadata from assessment (singular) api.
   */
  const openDecisionTree = async (assessItem) => {
    // load selected assessment for metadata
    setSelectedAssessId(assessItem.id)
    // set decision tree
    let dtData = getSelectedDt(assessItem.assessment_name)
    setSelectedDt(dtData)
    setTreeModalOpen(true)
  }

  const getRowHeight = ({ data }) => {
    if (data.activities) {
      const numRows = data.activities.length || 1
      return 35 * numRows
    }
  }

  const columnDefs = [
    {
      headerName: "Region",
      field: "region",
    },
    {
      headerName: "Assessment",
      field: "assessment_name",
      cellRenderer: AssessmentLinkCellRenderer,
      cellRendererParams: { studyId },
    },
    {
      headerName: "Tags",
      field: "tags",
      cellRenderer: ({ value }) => {
        return (
          <Tag.Group
            tags={value}
            maxTags={5}
          />
        )
      },
    },
    {
      headerName: "Type",
      field: "sub_type",
    },
    {
      field: "registry",
      cellRenderer: "RegistryCellRenderer",
      cellClass: ["col-badge"],
    },
    {
      headerName: "Result",
      field: "result",
      cellRenderer: AssessmentResultCellRenderer,
      cellRendererParams: {
        setMissingFields,
        openDecisionTree,
      },
      filterParams: {
        valueFormatter: ({ value }) => {
          if (value === "Not Yet Assessed") return "Insufficient Data"
          return value
        },
      },
      cellClass: ["col-badge"],
    },
    {
      headerName: "Due",
      field: "assessment_due_date",
      cellRenderer: DueDateCellRenderer,
      type: "date",
    },
    {
      headerName: "Activity",
      field: "attached_projects",
      cellRenderer: ActivityCellRendererTable,
      cellStyle: { padding: 0 },
      cellRendererParams: {
        activityTypes,
        toggleModal: toggleCreateActivityModal,
        permissions,
      },
      valueGetter: ({ data }) => {
        if (data.attached_projects) return data.attached_projects.map((actObj) => actObj.title)
        return null
      },
      cellClass: ["assessment-grid-activity-cell"],
    },
  ]

  return (
    <div className="assessments-grid-container">
      <GridTypeA
        gridRef={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        onFilterChanged={onFilterChanged}
        getRowHeight={getRowHeight}
        domLayout="autoHeight"
        onGridReady={onGridReady}
        className="assessments-grid"
      />
      {selectedDt && selectedAssessment && (
        <DecisionTreeModal
          dt={{ schema: selectedAssessment?.res_schema }}
          assessment={selectedAssessment}
          showModal={treeModalOpen}
          onHide={() => {
            setTreeModalOpen(false)
            setSelectedDt(null)
          }}
        />
      )}
    </div>
  )
}

export default AssessmentsGrid
